import React from "react";
import {
  Button,
  Collapse,
  ColorPicker,
  ConfigProvider,
  Select,
  theme,
} from "antd";
import { useTranslation } from "react-i18next";
import { EMAIL_FONTS, EmailStyles, defaultEmailStyles } from "../../blocks";
import { CornersField, SpacingField } from "../Fields";

import PanelTitle from "../PanelTitle";

import styles from "./index.module.scss";

interface StylePanelProps {
  value: EmailStyles;
  onChange: (patch: Partial<EmailStyles>) => void;
}

/**
 * The letter's palette, grouped by the ground each colour belongs to.
 *
 * Not by kind — all the text colours together, all the surfaces together — but
 * by what is being built: a block on the sheet needs its ground, its words, its
 * quiet words and its rules, and they are chosen in one go. The brand keeps its
 * own pair, since a button carries it whatever ground it stands on.
 */
const COLOUR_GROUPS: { title: string; keys: (keyof EmailStyles)[] }[] = [
  { title: "groupBrand", keys: ["brand", "textOnBrand"] },
  // Directly over the sheet's own colours, because that is where it is on
  // screen: the ground the letter stands on, then the letter. It keeps a group
  // of its own rather than joining Base — nothing is ever set in it, nothing is
  // read against it, so it has none of the pairs the runs below are made of.
  { title: "groupEmail", keys: ["emailBackground"] },
  {
    title: "groupBackground",
    keys: [
      "background",
      "subtleBackground",
      "textOnBackground",
      "textMutedOnBackground",
      "link",
      "border",
    ],
  },
  {
    title: "groupAccent",
    keys: [
      "accent",
      "textOnAccent",
      "textMutedOnAccent",
      "linkOnAccent",
      "borderOnDark",
    ],
  },
];

export default function StylePanel({ value, onChange }: StylePanelProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  /** One colour of the letter, as the row the panel shows it in. */
  const colour = (key: keyof EmailStyles) => (
    <label key={key} className={styles.field}>
      {t(`pages.campaigns.styles.${key}`)}
      {/* antd's own picker — hue, alpha, hex and presets are all in it. */}
      <ColorPicker
        showText
        // Alpha is kept: two of these colours are a light one worn thin, and
        // toHexString would flatten them to opaque.
        value={value[key] as string}
        onChangeComplete={(color) =>
          onChange({ [key]: color.toRgbString() } as Partial<EmailStyles>)
        }
      />
    </label>
  );

  /**
   * The panel, in the order it is read: the face first — it is the one setting
   * that changes every word, and the colours are read in it — then the grounds
   * from the outside in, then what applies to every block whatever colour it
   * is.
   */
  const GROUPS: { title: string; render: () => React.ReactNode }[] = [
    {
      title: "groupType",
      render: () => (
        <label className={styles.field}>
          {t("pages.campaigns.styles.fontFamily")}
          <Select
            value={value.fontFamily}
            onChange={(fontFamily) => onChange({ fontFamily })}
            options={EMAIL_FONTS.map((font) => ({
              value: font.stack,
              // Each name drawn in its own face: the list is a choice between
              // typefaces, and names all set in the same one say nothing.
              label: (
                <span style={{ fontFamily: font.stack }}>{font.label}</span>
              ),
            }))}
          />
        </label>
      ),
    },
    ...COLOUR_GROUPS.map(({ title, keys }) => ({
      title,
      render: () => <>{keys.map(colour)}</>,
    })),
    {
      // All three apply to every block: an email is a stack of them, and how
      // round they are and how much air they keep are properties of the letter
      // rather than of any one block. A block can still answer for itself.
      title: "groupLayout",
      render: () => (
        <>
          <div className={styles.field}>
            {t("pages.campaigns.builder.blockPaddings")}
            <SpacingField
              value={value.padding}
              onChange={(padding) => onChange({ padding })}
            />
          </div>

          <div className={styles.field}>
            {t("pages.campaigns.builder.blockCorners")}
            <CornersField
              value={value.radius}
              onChange={(radius) => onChange({ radius })}
            />
          </div>

          <div className={styles.field}>
            {t("pages.campaigns.builder.imageCorners")}
            <CornersField
              value={value.imageRadius}
              onChange={(imageRadius) => onChange({ imageRadius })}
            />
          </div>
        </>
      ),
    },
  ];

  return (
    <div className={styles.panel}>
      {/* The same heading the Blocks panel and the block editor open with, so
          the three tabs start at the same place. */}
      <PanelTitle>{t("pages.campaigns.builder.settings")}</PanelTitle>

      {/* Every group folds, one at a time. antd's own Collapse in `accordion`
          mode, at its small size — the component, its dividers, its box and its
          spacing as the library draws them, with nothing of ours over the top.
          It arrives closed, so the panel opens as a list of what the letter has
          rather than as everything it has at once. */}
      <ConfigProvider
        theme={{
          components: {
            // The grounds, through antd's own tokens rather than over its
            // classes: the panel is already a surface, and a Collapse painting
            // its headers grey inside it made a box within a box.
            Collapse: { headerBg: "transparent", contentBg: "transparent" },
          },
        }}
      >
        <Collapse
          accordion
          size="small"
          className={styles.groups}
          items={GROUPS.map(({ title, render }) => ({
            key: title,
            // The header's own type, not ours. It is laid out for antd's 14px
            // heading text — its line box, the height of its arrow and the
            // alignment between the two are measured from that — and putting
            // 12px uppercase secondary text in it left the label sitting in a
            // line box built for something else.
            label: t(`pages.campaigns.styles.${title}`),
            children: <div className={styles.group}>{render()}</div>,
          }))}
        />
      </ConfigProvider>

      {/* Twice the panel's own rhythm above it, the same distance the block
          editor keeps over Remove: the one control here that undoes everything
          should not sit a field's width from the last setting. */}
      <Button
        block
        className={styles.reset}
        // No dialog and no message. It is a change to the letter like any
        // other, so it goes into the letter's history and Undo takes it back —
        // which is both the safety net and the thing the user already knows how
        // to reach for.
        onClick={() => onChange(defaultEmailStyles(token))}
      >
        {t("pages.campaigns.styles.reset")}
      </Button>
    </div>
  );
}
